/**
 * OCT28 #9 (field test 2026-10-28) — an end-of-day overrun with NO decision record (no proposed rollover: e.g. started
 * single-day work running long, or the head of an approved multi-day plan) used to offer nothing to act on. Each such
 * row now offers the real, existing decision paths:
 *   - "Authorize overtime" — only for a person the server allows (can_i_authorize_overtime), with a stated reason,
 *     through authorize_ticket_overtime (audited; the alert clears once the committed finish is within it). Nothing is
 *     authorized automatically, and the server still refuses anyone else.
 *   - "Open ticket" — reassign, reschedule or re-run OptiPlace from the ticket itself.
 * The alert is never hidden here; it clears only when the schedule actually fits.
 */
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { describeDecisionRefusal } from '@/utils/rolloverDecisionMessages';

type LooseDb = { rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: any; error: { message: string } | null }> };

export interface EodOverrunActionsProps {
  ticketId: string;
  workDate: string;
  boundaryAt: string | null;
  projectedFinish: string | null;
  overrunMin: number;
  mayAuthorizeOvertime: boolean;
  /** OCT28 #9: the head of an approved multi-day plan extends TODAY ("Extend today: authorize N min overtime…"). */
  overtimeVerb?: string;
}

export function overtimeRequest(p: Pick<EodOverrunActionsProps, 'boundaryAt' | 'projectedFinish' | 'overrunMin'>):
  { boundary: string; until: string; minutes: number } | null {
  const finish = p.projectedFinish ? Date.parse(p.projectedFinish) : NaN;
  if (!Number.isFinite(finish) || p.overrunMin <= 0) return null;
  const boundaryMs = p.boundaryAt && Number.isFinite(Date.parse(p.boundaryAt)) ? Date.parse(p.boundaryAt) : finish - p.overrunMin * 60_000;
  if (finish <= boundaryMs) return null;
  return { boundary: new Date(boundaryMs).toISOString(), until: new Date(finish).toISOString(), minutes: Math.ceil((finish - boundaryMs) / 60_000) };
}

export function EodOverrunActions(props: EodOverrunActionsProps) {
  const db = supabase as unknown as LooseDb;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [requestId] = useState(() => crypto.randomUUID());   // one authorization per opening (idempotent)
  const req = overtimeRequest(props);

  const authorize = async () => {
    if (!req || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const { data, error } = await db.rpc('authorize_ticket_overtime', {
        p_ticket: props.ticketId, p_work_date: props.workDate, p_until: req.until, p_reason: reason.trim(),
        p_request_id: requestId, p_boundary: req.boundary, p_conversion: null,
      });
      if (error || !['AUTHORIZED', 'REPLAY', 'ALREADY_AUTHORIZED'].includes(data?.outcome)) {
        setResult(`Overtime not authorized: ${describeDecisionRefusal(error?.message ?? data?.cause ?? data?.outcome)}`);
      } else {
        setResult(data?.outcome === 'ALREADY_AUTHORIZED'
          ? 'Overtime was already authorized for this ticket today.'
          : `Authorized ${req.minutes} min of overtime. The alert clears once the schedule is re-checked.`);
        setOpen(false);
      }
    } catch (e) {
      setResult(`Overtime not authorized: ${describeDecisionRefusal((e as Error)?.message)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1 space-y-1" data-testid={`eod-overrun-actions-${props.ticketId}`}>
      <div className="flex flex-wrap gap-2">
        <a className="min-h-[44px] inline-flex items-center rounded border border-red-600 px-3 text-xs font-medium"
           href={`/?tab=dayboard&highlight=${encodeURIComponent(props.ticketId)}&openDetails=true`}
           data-testid={`eod-open-ticket-${props.ticketId}`}>
          Open ticket (reassign / reschedule)
        </a>
        {props.mayAuthorizeOvertime && req && !open && (
          <button type="button" className="min-h-[44px] rounded border border-red-600 px-3 text-xs font-medium"
                  data-testid={`eod-authorize-overtime-${props.ticketId}`} onClick={() => { setOpen(true); setResult(null); }}>
            {props.overtimeVerb ?? 'Authorize'} {req.minutes} min overtime…
          </button>
        )}
      </div>
      {open && req && (
        <div className="space-y-1" data-testid={`eod-overtime-form-${props.ticketId}`}>
          <label className="block text-xs" htmlFor={`eod-ot-reason-${props.ticketId}`}>Why is this overtime authorized? (required)</label>
          <input id={`eod-ot-reason-${props.ticketId}`} className="w-full rounded border px-2 py-1 text-xs text-foreground bg-background"
                 value={reason} disabled={busy} maxLength={500} onChange={(e) => setReason(e.target.value)}
                 data-testid={`eod-ot-reason-${props.ticketId}`} />
          <div className="flex gap-2">
            <button type="button" className="min-h-[44px] rounded bg-red-600 px-3 text-xs font-medium text-white disabled:opacity-50"
                    disabled={busy || reason.trim().length < 3} onClick={() => void authorize()}
                    data-testid={`eod-ot-confirm-${props.ticketId}`}>
              {busy ? 'Authorizing…' : `Authorize ${req.minutes} min`}
            </button>
            <button type="button" className="min-h-[44px] px-3 text-xs underline" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
      {result && <div className="text-xs" role="status" data-testid={`eod-ot-result-${props.ticketId}`}>{result}</div>}
    </div>
  );
}
